import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import useTranslation from '../../Hooks/useTranslation'
import { useAuth } from '../../Contexts/AuthProvider'
import { useSelector } from 'react-redux'
import { useQuery, useQueryClient } from 'react-query'
import axios from 'axios'
import { apiAuthBaseUrl } from '../../config'
import DmsService from '../../Services/DMS/dms-api.service'

const UsersSettingsContext = React.createContext(undefined)

const UsersSettingsProvider = ({children}) => {

    const translation = useTranslation()
    const queryClient = useQueryClient()
    const { currentUser, userLoaded } = useAuth()
    const currentCompany = useSelector(state => state.dashboard.currentCompany)
    const [companyDetails, setCompanyDetails] = useState(null)

    const [usersTab, setUsersTab] = useState(`${translation.settingsTablesUsersLabel} (?)`)
    const [oneJobPasswordTab, setOneJobPasswordTab] = useState(`${translation.settingsTablesOneJobPasswordsLabel} (?)`)
    const [dbManagementTab, setDbManagementTab] = useState(`${translation.settingsDbManagementLabel}`)
    const [templatesManagementTab] = useState(`${translation.settingsTemplatesManagementLabel}`)
    const [jobConfigurationTab] = useState(`${translation.settingsJobConfigurationLabel}`)
    const [jobPlannerTab] = useState(`${translation.settingsJobPlannerLabel}`)
    const [unitSettingsTab] = useState(`${translation.settingsUnitSettingsLabel}`)
    const [fleetViewSettingsTab] = useState(`${translation.settingsFleetViewSettingsLabel || translation.dataUsageFleetViewSettingsTabLabel || 'Fleet View Settings'}`)
    const [integrationsTab] = useState(`${translation.settingsIntegrationsLabel}`)
    const [devicesTab] = useState(`${translation.settingsDevicesLabel}`)
    const [apiKeysTab] = useState(`${translation.settingsApiKeysLabel || 'API Keys'}`)
    const [pageHeaderActions, setPageHeaderActions] = useState(null)

    const [users, setUsers] = useState([])
    const [oneJobPasswords, setOneJobPasswords] = useState([])
    const [oneJobPasswordFilterType, setOneJobPasswordFilterType] = useState('job')
    const [pageNumber, setPageNumber] = useState(1)
    const [pageSize, setPageSize] = useState(15)
    const [pagination, setPagination] = useState(null)
    const [sortColumn, setSortColumn] = useState('Id')
    const [sortDirection, setSortDirection] = useState('Asc')
    const prevFilterTypeRef = useRef('job')
    
    const setOneJobPasswordFilterTypeWithReset = useCallback((newType) => {
        if (prevFilterTypeRef.current !== newType) {
            queryClient.cancelQueries(['settingsOneJobPasswords'], { exact: false })
            queryClient.removeQueries(['settingsOneJobPasswords'], { exact: false })
            prevFilterTypeRef.current = newType
            setPageNumber(1)
            setOneJobPasswordFilterType(newType)
        }
    }, [queryClient])

    //fetch users for non-root current user
    const usersRequest = useQuery(
        ['settingsUsers', currentCompany],
        () => {
            const config = ! currentUser.isSuperuser() ? {} : {
                headers: {
                    'CompanyId': currentCompany?.id
                }
            }
            return axios.get(`${apiAuthBaseUrl}/users`, config)
        },
        {keepPreviousData: true, enabled: userLoaded}
    )

    useQuery(
        'company',
        () =>  {
            const companyId = currentUser.companyId
            return axios.get(`${apiAuthBaseUrl}/companies/${companyId}`)
        },
        {
            enabled: userLoaded && !currentUser.isSuperuser(),
            onSuccess: data => setCompanyDetails(data.data)
        }
    )

    const oneJobPasswordsRequest = useQuery(
        ['settingsOneJobPasswords', currentCompany, oneJobPasswordFilterType, pageNumber, pageSize, sortColumn, sortDirection],
        () => {
            const typeParam = oneJobPasswordFilterType === 'unit' ? 'Unit' : 'Job'
            const request = {
                pageNumber,
                pageSize,
                type: typeParam,
                sortColumn,
                sortDirection
            }
            const config = ! currentUser.isSuperuser() ? {} : {
                headers: {
                    'CompanyId': currentCompany?.id
                }
            }
            return DmsService.getOneJobPasswordsPaged(request, config)
        },
        {
            keepPreviousData: false,
            enabled: userLoaded,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            staleTime: 0,
            cacheTime: 0,
            onSuccess: (response) => {
                if (response?.data) {
                    const data = response.data
                    setOneJobPasswords(data.items || [])
                    setPagination({
                        pageCount: data.pageCount || 0,
                        itemsCount: data.itemsCount || 0,
                        itemsTotalCount: data.itemsTotalCount || 0,
                        pageNumber: data.pageNumber || 0,
                        pageSize: data.pageSize || 0,
                    })
                }
            }
        }
    )

    
    const allOneJobPasswordsRequest = useQuery(
        ['settingsAllOneJobPasswords', currentCompany],
        () => {
            const config = ! currentUser.isSuperuser() ? {} : {
                headers: {
                    'CompanyId': currentCompany?.id
                }
            }
            return axios.get(`${apiAuthBaseUrl}/JobPasswords`, config)
        },
        {keepPreviousData: true, enabled: userLoaded}
    )

    const updateUsersTab = useCallback(() => setUsersTab(`${translation.settingsTablesUsersLabel} (${users.length})`), [translation, users])
    const updateOneJobPasswordTab = useCallback(() => {
        const allPasswordsCount = allOneJobPasswordsRequest?.data?.data?.length || 0
        setOneJobPasswordTab(`${translation.settingsTablesOneJobPasswordsLabel} (${allPasswordsCount})`)
    }, [translation, allOneJobPasswordsRequest])
    const updateDbManagementTab = useCallback((count) => setDbManagementTab(`${translation.settingsDbManagementLabel}`), [translation])


    useEffect(() => {
        if (currentUser && currentUser.isSuperuser()) {
            setCompanyDetails(currentCompany)
        }
    }, [currentCompany, currentUser])

    useEffect(() => {
        if (usersRequest.isSuccess) {
            setUsers(usersRequest.data.data)
        }
    }, [
        usersRequest.isSuccess,
        usersRequest.data,
    ])



    useEffect(() => {
        updateUsersTab()
        updateOneJobPasswordTab()
        updateDbManagementTab('?')
    }, [
        translation,
        updateUsersTab,
        updateOneJobPasswordTab,
        updateDbManagementTab,
        allOneJobPasswordsRequest.data
    ])

    const allowDevices = useMemo(() => currentUser.allowUiDevices(), [currentUser])
    // Fleet View Settings should be visible only for SuperUser/Master AND only when enabled in `me` (allowUiFleetView).
    const canShowFleetViewSettings = useMemo(
        () => currentUser.allowUiFleetView() && (currentUser.isSuperuser() || currentUser.isMaster()),
        [currentUser],
    )
    const allowUiApiKeys = useMemo(() => currentUser.allowUiApiKeys(), [currentUser])

    const tabs = useMemo(() => {
        const items =  [
            {label: jobPlannerTab, title: translation.settingsJobPlannerLabel, path: 'job-planner', id: 'settingsJobPlannerTab'},
            {label: usersTab, title: translation.settingsTablesUsersLabel, path: 'users', id: 'settingsUsersTab'},
            {label: oneJobPasswordTab, title: translation.settingsTablesOneJobPasswordsLabel, path: 'one-job-password', id: 'settingsOneJobPasswordsTab'},
        ]
        if (allowUiApiKeys) {
            items.push({
                label: apiKeysTab,
                title: translation.settingsApiKeysLabel || 'API Keys',
                path: 'api-keys',
                id: 'settingsApiKeysTab',
            })
        }
        if (canShowFleetViewSettings) {
            items.push({
                label: fleetViewSettingsTab,
                title: translation.settingsFleetViewSettingsLabel || translation.dataUsageFleetViewSettingsTabLabel || 'Fleet View Settings',
                path: 'fleet-view-settings',
                id: 'settingsFleetViewSettingsTab',
            })
        }
        items.push({label: dbManagementTab, title: translation.settingsDbManagementLabel, path: 'db-management', id: 'settingsDbManagementTab'})
        if (currentUser.canManageTemplates()) {
            items.push({label: templatesManagementTab, title: translation.settingsTemplatesManagementLabel, path: 'templates-management', id: 'settingsTemplatesManagementTab'})
        }
        items.push({label: integrationsTab, title: translation.settingsIntegrationsLabel, path: 'integrations', id: 'settingsIntegrationsTab'});
        if (allowDevices) {
            items.push({label: devicesTab, title: translation.settingsDevicesLabel, path: 'devices', id: 'settingsDevicesTab'});
        }
        items.push({label: jobConfigurationTab, title: translation.settingsJobConfigurationLabel, path: 'job-configuration', id: 'settingsJobConfigurationTab'});
        items.push({label: unitSettingsTab, title: translation.settingsUnitSettingsLabel, path: 'unit-settings', id: 'settingsUnitSettingsTab'});
        return items;
    }, [usersTab, oneJobPasswordTab, dbManagementTab, integrationsTab, currentUser, templatesManagementTab, devicesTab, allowDevices, jobConfigurationTab, jobPlannerTab, unitSettingsTab, fleetViewSettingsTab, canShowFleetViewSettings, apiKeysTab, allowUiApiKeys, translation])


    const updateUsers = useCallback(() => {
        queryClient.invalidateQueries('settingsUsers').then()
    }, [queryClient])

    const onNextPage = useCallback(() => {
        setPageNumber(p => p + 1)
        window.scrollTo(0, 0)
    }, [])

    const onPrevPage = useCallback(() => {
        setPageNumber(p => p - 1)
        window.scrollTo(0, 0)
    }, [])

    const handleRowCount = useCallback((count) => {
        setPageSize(count)
        setPageNumber(1)
    }, [])

    const handleSort = useCallback((column) => {
        if (sortColumn === column) {
            setSortDirection(prev => prev === 'Asc' ? 'Desc' : 'Asc')
        } else {
            setSortColumn(column)
            setSortDirection('Asc')
        }
        setPageNumber(1)
    }, [sortColumn])


    return (
        <UsersSettingsContext.Provider value={{
            tabs,
            pageHeaderActions,
            setPageHeaderActions,
            updateUsersTab,
            updateOneJobPasswordTab,
            currentUser,
            currentCompany,
            companyDetails,
            setCompanyDetails,
            users,
            oneJobPasswords,
            updateUsers,
            oneJobPasswordFilterType,
            setOneJobPasswordFilterType: setOneJobPasswordFilterTypeWithReset,
            pagination,
            oneJobPasswordsRequest,
            allOneJobPasswordsRequest,
            onNextPage,
            onPrevPage,
            handleRowCount,
            sortColumn,
            sortDirection,
            handleSort
        }}>
            {children}
        </UsersSettingsContext.Provider>
    )
}

const useUsersSettings = () => {
    const context = React.useContext(UsersSettingsContext)
    if (context === undefined) {
        throw new Error('useUsersSettings must be used within a UsersSettingsProvider')
    }
    return context
}

export {
    UsersSettingsProvider,
    useUsersSettings
}
